import type { ChangeOrderStatus } from '@buildbee/contracts';
import { documentTotals, lineTotals, type PricedLine } from '../common';

/**
 * Change management: change events (potential changes with cost and revenue estimates) turn
 * into prime change orders (owner contract) and commitment change orders (subcontracts / POs).
 */

export type ChangeLine = PricedLine & { commitmentId?: string | null };

export type ChangeOrderTotals = {
  lines: { costTotalCents: number; priceTotalCents: number }[];
  costSubtotalCents: number;
  subtotalCents: number;
  taxCents: number;
  totalCents: number;
  /** Price - cost (markup earned). */
  markupCents: number;
};

/** Prime change order totals: lines priced with markup, tax on taxable lines. */
export function changeOrderTotals(lines: PricedLine[], opts: { markupBps?: number; taxRateBps?: number } = {}): ChangeOrderTotals {
  const perLine = lines.map((l) => lineTotals(l, opts.markupBps ?? 0));
  const t = documentTotals(lines, opts);
  return { lines: perLine, ...t, markupCents: t.subtotalCents - t.costSubtotalCents };
}

/** Commitment change order / commitment totals: cost only (no markup, no tax). */
export function costTotals(lines: { quantity: number; unitCostCents: number }[]) {
  const perLine = lines.map((l) => lineTotals({ ...l, markupBps: 0 }).costTotalCents);
  return { lines: perLine, totalCents: perLine.reduce((a, b) => a + b, 0) };
}

export type ChangeEventEstimate = {
  costCents: number;
  revenueCents: number;
  marginCents: number;
  /** Margin / revenue, basis points (0 when there is no revenue). */
  marginBps: number;
  /** Cost already assigned to commitments. */
  committedCostCents: number;
};

/** Rough order of magnitude for a change event: cost, revenue at markup, margin. */
export function changeEventEstimate(lines: ChangeLine[], defaultMarkupBps = 0): ChangeEventEstimate {
  let cost = 0;
  let revenue = 0;
  let committed = 0;
  for (const l of lines) {
    const t = lineTotals(l, defaultMarkupBps);
    cost += t.costTotalCents;
    revenue += t.priceTotalCents;
    if (l.commitmentId) committed += t.costTotalCents;
  }
  const margin = revenue - cost;
  return {
    costCents: cost,
    revenueCents: revenue,
    marginCents: margin,
    marginBps: revenue !== 0 ? Math.round((margin * 10_000) / revenue) : 0,
    committedCostCents: committed,
  };
}

/** Lines of a change event grouped by the commitment they affect (lines without one are skipped). */
export function groupByCommitment<T extends { commitmentId?: string | null }>(lines: T[]): Map<string, T[]> {
  const out = new Map<string, T[]>();
  for (const l of lines) {
    if (!l.commitmentId) continue;
    const list = out.get(l.commitmentId) ?? [];
    list.push(l);
    out.set(l.commitmentId, list);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Prime change order workflow
// ---------------------------------------------------------------------------

const TRANSITIONS: Record<ChangeOrderStatus, readonly ChangeOrderStatus[]> = {
  draft: ['pending_internal', 'sent', 'approved', 'void'],
  pending_internal: ['draft', 'sent', 'approved', 'rejected', 'void'],
  sent: ['approved', 'rejected', 'draft', 'void'],
  approved: ['void'],
  rejected: ['draft', 'void'],
  void: [],
};

export function canTransitionChangeOrder(from: ChangeOrderStatus, to: ChangeOrderStatus): boolean {
  return TRANSITIONS[from].includes(to);
}

/** Only drafts (and rejected orders reopened as drafts) can be edited. */
export function isChangeOrderEditable(status: ChangeOrderStatus): boolean {
  return status === 'draft' || status === 'pending_internal';
}

/** Statuses whose amounts count as "pending" exposure on the budget. */
export function isPendingChange(status: ChangeOrderStatus): boolean {
  return status === 'pending_internal' || status === 'sent';
}
